import React, { Component } from 'react'
import { connect } from 'react-redux';

import get from 'lodash/get'
import { Link, Redirect } from 'react-router-dom'

import LoadingOverlay from './LoadingOverlay'

import { LoginForm } from './LoginForm'
import { Message } from './Message'
import { AccountMigrate } from './AccountMigrate'

import { authenticate, webfinger } from '../../../lib/authentication'
import { createIdpRedirectUri, createRedirectParamsCookie } from '../../../config/okta-config'

import { createIdentityMap } from '../utils'

import routes from '../../constants/routes'

import { isAuthenticated } from '../selectors'

import './auth.scss'

const Stages = createIdentityMap(
  'ENTERING_PASSWORD',
  'ENTERING_USERNAME',
  'IDP_DISCOVERY',
  'IDP_REDIRECT',
  'SUBMITTING_CREDENTIALS',
)

const AccountMigrated = 'Account Migrated';

const AUTHENTICATION_ERROR_SUMMARY = 'Authentication Failed'
const AUTHENTICATION_ERROR_MESSAGE = 'Incorrect username or password.'
const AUTHENTICATION_RESOURCE_NOT_FOUND = 'Not found: Resource not found'
const USER_DISABLED_ERROR_SUMMARY = 'User is disabled'
const USER_DISABLED_ERROR_MESSAGE = 'User is disabled. If you believe this is an error, please contact the administrator.'
const USER_DEACTIVATED_ERROR_SUMMARY = 'User is deactivated'
const USER_DEACTIVATED_ERROR_MESSAGE = 'User is deactivated! If you believe this is an error, please contact the administrator.'
const USER_LOCKED = 'Account locked'

class Login extends Component {
  constructor(props) {
    super(props)

    this.state = {
      username: '',
      password: '',
      error: { errorSummary: '', errorItems: [] },
      stage: Stages.ENTERING_USERNAME,
      idpUrl: '',
    }
  }

  setError(errorSummary, ...errorItems) {
    this.setState({ error: { errorSummary, errorItems } })
  }

  setStage(stage) {
    this.setState({ stage })
  }

  isOnStage(...stages) {
    return stages.includes(this.state.stage)
  }

  isFederatedUser(finger) {
    return get(finger, 'links.0.properties.okta:idp:id', null) !== null
  }

  getIdpUrl(finger, username) {
    const idpId = get(finger, 'links.0.properties.okta:idp:id')
    return createIdpRedirectUri(idpId)
  }

  handleChange = (event) => {
    const target = event.target.id
    const originalValue = this.state[event.target.id]
    const newValue = event.target.value

    let newState = { [target]: newValue }

    if (newValue !== originalValue && event.target.id === 'username' && this.isOnStage(Stages.ENTERING_PASSWORD)) {
      this.setStage(Stages.ENTERING_USERNAME)
      newState = { ...newState, password: '' }
    }

    this.setState(newState)
  }

  handleSubmitForm = async (event) => {
    event.preventDefault()

    const { username, password } = this.state

    let usernameTrimmed= username.trim().toLowerCase();
    this.setError()

    if (this.isOnStage(Stages.ENTERING_USERNAME)) {
      this.setStage(Stages.IDP_DISCOVERY)

      const finger = await webfinger(usernameTrimmed)

      if (!this.isFederatedUser(finger)) {
        this.setStage(Stages.ENTERING_PASSWORD)
        return
      }

      const idpUrl = this.getIdpUrl(finger, usernameTrimmed)

      if (idpUrl) {
        document.cookie = createRedirectParamsCookie()
        this.setState({ idpUrl })
        this.setStage(Stages.IDP_REDIRECT)
        return
      }
    }

    if (this.isOnStage(Stages.ENTERING_PASSWORD)) {
      this.setStage(Stages.SUBMITTING_CREDENTIALS)

      const { success, error } = await authenticate(usernameTrimmed, password)

      if (!success && error) {
        const { errorSummary = '', errorItems = [] } = error
        this.setError(errorSummary, ...errorItems)
        this.setStage(Stages.ENTERING_PASSWORD)
        return
      }

      if (success && !error) {
        return
      }
    }

    this.setError('Unknown Error occurred', 'Please try again')
    this.setStage(Stages.ENTERING_PASSWORD)
  }

  validateForm = () => {
    const { username, password } = this.state

    const valid = []

    if (this.isOnStage(Stages.ENTERING_USERNAME)) {
      valid.push(username.length)
    }

    if (this.isOnStage(Stages.ENTERING_PASSWORD)) {
      valid.push(username.length)
      valid.push(password.length)
    }

    return valid.every(Boolean)
  }

  renderForm() {
    const { username, password, error } = this.state

    const isEnteringUsername = this.isOnStage(Stages.ENTERING_USERNAME)
    const isIdpDiscovery = this.isOnStage(Stages.IDP_DISCOVERY)

    const autoFocusInput = isEnteringUsername && 'username' || 'password'
    const hideInputs = [(isEnteringUsername || isIdpDiscovery) && 'password' || false].filter(Boolean)
    const isBusy = this.isOnStage(Stages.IDP_DISCOVERY, Stages.SUBMITTING_CREDENTIALS)
    return (
      <div>{error && error.errorSummary === USER_LOCKED && this.renderErrors()}
      <LoginForm
        onSubmit={this.handleSubmitForm}
        onChange={this.handleChange}
        validateForm={this.validateForm}
        username={username}
        password={password}
        autoFocusInput={autoFocusInput}
        hideInputs={hideInputs}
        isBusy={isBusy}
      >
        { error && error.errorSummary !== USER_LOCKED && this.renderErrors()}
      </LoginForm>
      </div>
    )
  }

  renderErrors() {
    const { error: { errorSummary = 'Unknown Error Ocurred', errorItems = [] } = {} } = this.state
    if (errorSummary === AUTHENTICATION_ERROR_SUMMARY || errorSummary.includes(AUTHENTICATION_RESOURCE_NOT_FOUND)) {
      return (
        <div className="error-message">
          {AUTHENTICATION_ERROR_MESSAGE}
        </div>
      )
    }
    if (errorSummary === USER_DISABLED_ERROR_SUMMARY) {
      return (
        <div className="error-message">
          {USER_DISABLED_ERROR_MESSAGE}
        </div>
      )
    }
    if (errorSummary === USER_DEACTIVATED_ERROR_SUMMARY) {
      return (
        <div className="error-message">
          {USER_DEACTIVATED_ERROR_MESSAGE}
        </div>
      )
    }
    if (!errorItems.length) return null
    return (
      <Message title={errorSummary} type="danger">
        {errorItems.map(errorItem => (<p key={btoa(errorItem)}>{errorItem}</p>))}
      </Message>
    )
  }

  renderInvalidInviteTokenMessage() {
    return (
      <Message title={'You have selected an invalid link'} type="info">
        The link you have selected is inactive, please check your email for a more recent version or if you have signed up, log in below
      </Message>
    )
  }

  renderHome() {
    return (
      <Redirect to={routes.SUBMISSIONS} />
    )
  }

  renderIdp(url) {
    return window.location.assign(url)
  }

  render() {
    const { error: { errorSummary } = {}, idpUrl } = this.state
    const { location: { state }, isAuthenticated } = this.props

    return (
      <div className="AuthContainer container vertical">
        {state && state.from === "inviteToken" && this.renderInvalidInviteTokenMessage()}
        {AccountMigrated === errorSummary ?
         <AccountMigrate username={this.state.username} />
        :
         this.renderForm()
        }
        {isAuthenticated && this.renderHome()}
        {this.isOnStage(Stages.IDP_REDIRECT) && this.renderIdp(idpUrl)}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  isAuthenticated: isAuthenticated(state),
})

export default connect(mapStateToProps)(Login)
